import { FilterProps } from 'components/table/enhancements/filters/filterTypes'
import {
  removeFilter,
  useClickOutside,
} from 'components/table/enhancements/filters/filterUtils'
import { FilterHeaderButton } from 'components/table/utility/FilterHeaderButton'
import { RemoveFilterButton } from 'components/table/utility/RemoveFilterButton'
import isNil from 'lodash/isNil'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  FilterContainer,
  LabelAndInputContainer,
  RelativeWrap,
  StyledCloseIcon,
  StyledInput,
} from 'styles/assetDatabaseStyles'

const NumberRangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`

const StyledNumberInput = styled(StyledInput)`
  padding-left: 0.5rem;
  width: 80px;
  min-width: 80px;

  &:focus-within {
    outline: ${({ theme }) => `1px solid ${theme.forecast.border}`};
  }
`

const StyledLabelAndInputContainer = styled(LabelAndInputContainer)`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
`

const StyledLabel = styled.label`
  text-align: right;
`

export const NumberFilter = <TData,>({
  column,
  columnHeader,
  setActiveFilters,
  navigate,
  disableUrlFilters,
}: FilterProps<TData>) => {
  const filterContainerRef = useRef<HTMLDivElement>(null)
  const [isInputVisible, setInputVisible] = useState(true)
  const [currentFilterValue, setCurrentFilterValue] = useState<
    [number | undefined, number | undefined]
  >([undefined, undefined])
  const columnFilterValue = column.getFilterValue() as [
    number | undefined,
    number | undefined,
  ]

  useEffect(() => {
    if (columnFilterValue && columnFilterValue !== currentFilterValue) {
      setCurrentFilterValue(columnFilterValue)
      setInputVisible(false)
    }
  }, [columnFilterValue, currentFilterValue])

  const handleInputChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      const numValue = value === '' ? undefined : Number(value)
      const newValue = [...currentFilterValue] as [
        number | undefined,
        number | undefined,
      ]
      newValue[index] = numValue

      column.setFilterValue(
        newValue[0] !== undefined || newValue[1] !== undefined
          ? newValue
          : undefined
      )
      setCurrentFilterValue(newValue)

      // Update URL parameters (only if URL filters are enabled)
      if (!disableUrlFilters) {
        if (newValue[0] !== undefined || newValue[1] !== undefined) {
          navigate({
            to: '.',
            search: (prev) => ({
              ...prev,
              [column.id]: newValue,
            }),
          })
        } else {
          removeFilter(column, setActiveFilters, navigate)
        }
      }
    }

  const closeFilters = () => {
    if (
      currentFilterValue[0] === undefined &&
      currentFilterValue[1] === undefined
    ) {
      removeFilter(column, setActiveFilters, navigate)
    } else {
      setInputVisible(false)
    }
  }

  useClickOutside(filterContainerRef, closeFilters)

  const closeOnEscape = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      closeFilters()
    }
  }

  const getDisplayValue = () => {
    const [min, max] = currentFilterValue
    if (!(isNil(min) || isNil(max))) {
      return `${min} - ${max}`
    } else if (!isNil(min)) {
      return `>= ${min}`
    } else if (!isNil(max)) {
      return `<= ${max}`
    }
    return ''
  }

  return (
    <RelativeWrap>
      <FilterHeaderButton
        onClick={() => setInputVisible((visible) => !visible)}>
        {columnHeader}
        {getDisplayValue() && (
          <RemoveFilterButton
            onClick={() => removeFilter(column, setActiveFilters, navigate)}>
            {getDisplayValue()}
            <StyledCloseIcon />
          </RemoveFilterButton>
        )}
      </FilterHeaderButton>
      {isInputVisible && (
        <FilterContainer
          ref={filterContainerRef}
          data-input-visible={isInputVisible ? 'true' : undefined}
          onKeyDown={closeOnEscape}>
          <NumberRangeContainer>
            <StyledLabelAndInputContainer>
              <StyledLabel>{'>='}</StyledLabel>
              <StyledNumberInput
                type='number'
                value={currentFilterValue[0] ?? ''}
                aria-label='Filter to at least this value'
                onChange={handleInputChange(0)}
                placeholder='min'
              />
            </StyledLabelAndInputContainer>
            <StyledLabelAndInputContainer>
              <StyledLabel>{'<='}</StyledLabel>
              <StyledNumberInput
                type='number'
                value={currentFilterValue[1] ?? ''}
                aria-label='Filter to at most this value'
                onChange={handleInputChange(1)}
                placeholder='max'
              />
            </StyledLabelAndInputContainer>
          </NumberRangeContainer>
        </FilterContainer>
      )}
    </RelativeWrap>
  )
}
